<template>
  <f-group :label="label" :id="id" :required="required" :tooltip="showTooltip ? tooltip : null" :hint="hint" :error="error">
    <multiselect
      :id="id"
      ref="multiselect"
      :class="{ 'is-invalid': !!error, 'multiselect--tooltip-overflow': hasTooltips }"
      :value="selected"
      :options="displayOptions"
      :multiple="multiple"
      :track-by="trackBy"
      :label="customLabel ? undefined : optionLabel"
      :custom-label="customLabel || undefined"
      :placeholder="placeholder"
      :loading="loading"
      :disabled="disabled"
      :searchable="searchable"
      :allow-empty="!required"
      select-label=""
      deselect-label=""
      selected-label=""
      @input="onSelect"
      @search-change="onSearchChange"
      @open="onOpen"
      @close="onClose"
    >
      <template slot="option" slot-scope="props">
        <span v-option-tooltip="optionTooltip(props.option)">{{ optionText(props.option) }}</span>
      </template>
      <template v-if="multiple && lockedIds.length" slot="tag" slot-scope="props">
        <span class="multiselect__tag">
          <span>{{ optionText(props.option) }}</span>
          <i
            v-if="!lockedIds.includes(props.option[trackBy])"
            aria-hidden="true"
            tabindex="1"
            class="multiselect__tag-icon"
            @keydown.enter.prevent="props.remove(props.option)"
            @mousedown.prevent="props.remove(props.option)"
          />
        </span>
      </template>
      <span slot="noResult">No results found.</span>
      <li v-if="!loading && !options.length && !searchQuery" slot="afterList" class="multiselect__element">
        <span class="multiselect__option">No results found.</span>
      </li>
      <li v-if="loadingMore" slot="afterList" class="multiselect__element">
        <span class="multiselect__option text-muted">Loading more…</span>
      </li>
    </multiselect>
  </f-group>
</template>

<script>
import AppMultiselect from '@/components/AppMultiselect'
import FGroup from './Base/FGroup'

// Sets data-tooltip on the closest .multiselect__option row (not the slot's inner span),
// so the tooltip's hover trigger is the whole already-full-size row vue-multiselect renders —
// no CSS box-model changes needed, and no dead zone at the row's own padding/boundary.
function setOptionTooltip(el, text) {
  const row = el.closest('.multiselect__option')
  if (!row) return
  if (text) {
    row.setAttribute('data-tooltip', text)
  } else {
    row.removeAttribute('data-tooltip')
  }
}

export default {
  name: 'FSearchSelect',
  components: { Multiselect: AppMultiselect, FGroup },
  directives: {
    optionTooltip: {
      inserted(el, binding) {
        setOptionTooltip(el, binding.value)
      },
      update(el, binding) {
        setOptionTooltip(el, binding.value)
      }
    }
  },
  props: {
    id: { type: String, required: true },
    label: String,
    value: { default: null },
    options: { type: Array, default: () => [] },
    trackBy: { type: String, default: 'id' },
    optionLabel: { type: String, default: 'name' },
    customLabel: { type: Function, default: null },
    optionTooltip: { type: Function, default: () => '' },
    placeholder: { type: String, default: 'Select...' },
    multiple: Boolean,
    lockedIds: { type: Array, default: () => [] },
    loading: Boolean,
    loadingMore: Boolean,
    disabled: Boolean,
    searchable: { type: Boolean, default: true },
    required: Boolean,
    showReset: { type: Boolean, default: true },
    resetLabel: { type: String, default: '— All —' },
    tooltip: String,
    hint: String,
    error: { type: String, default: null }
  },
  data() {
    return { searchQuery: '', scrollEl: null, onScroll: null }
  },
  computed: {
    resetOption() {
      return { [this.trackBy]: null, [this.optionLabel]: this.resetLabel }
    },
    displayOptions() {
      if (!this.showReset || this.required || this.multiple) return this.options
      return [this.resetOption, ...this.options]
    },
    selected() {
      if (this.multiple) {
        if (!this.value || !this.value.length) return []
        return this.options.filter(o => this.value.includes(o[this.trackBy]))
      }
      if (!this.value) return null
      return this.options.find(o => o[this.trackBy] === this.value) || null
    },
    showTooltip() {
      return this.required && !this.value
    },
    hasTooltips() {
      return this.options.some(o => !!this.optionTooltip(o))
    }
  },
  beforeDestroy() {
    this.detachScrollListener()
  },
  methods: {
    optionText(option) {
      if (option && option[this.trackBy] === null) return this.resetLabel
      return this.customLabel ? this.customLabel(option) : option[this.optionLabel]
    },
    onSearchChange(query) {
      this.searchQuery = query
      this.$emit('search-change', query)
    },
    onSelect(option) {
      if (this.multiple) {
        this.$emit('input', option ? option.map(o => o[this.trackBy]) : [])
        this.$emit('select', option)
        return
      }
      if (option && option[this.trackBy] === null) {
        this.$emit('input', null)
        this.$emit('select', null)
        return
      }
      this.$emit('input', option ? option[this.trackBy] : null)
      this.$emit('select', option)
    },
    onOpen() {
      this.$emit('open')
      this.$nextTick(this.attachScrollListener)
    },
    onClose() {
      this.detachScrollListener()
    },
    // Infinite scroll: vue-multiselect has no built-in "load more" hook, so we watch the
    // dropdown's own scroll container and emit load-more when the user nears the bottom.
    attachScrollListener() {
      const root = this.$refs.multiselect && this.$refs.multiselect.$el
      const wrapper = root && root.querySelector('.multiselect__content-wrapper')
      if (!wrapper) return
      this.scrollEl = wrapper
      this.onScroll = () => {
        if (wrapper.scrollHeight - wrapper.scrollTop - wrapper.clientHeight < 40) {
          this.$emit('load-more')
        }
      }
      wrapper.addEventListener('scroll', this.onScroll)
    },
    detachScrollListener() {
      if (this.scrollEl && this.onScroll) {
        this.scrollEl.removeEventListener('scroll', this.onScroll)
      }
      this.scrollEl = null
      this.onScroll = null
    }
  }
}
</script>

<style scoped>
/* Dropdowns with option tooltips (e.g. Draft Status) are short, fixed lists that never
   need to scroll — so it's safe to let the tooltip escape the option list's clipped bounds. */
.multiselect--tooltip-overflow >>> .multiselect__content-wrapper {
  overflow: visible;
}

/* .multiselect__option is already vue-multiselect's full-size row (position: relative,
   padding: 12px, min-height: 40px) — untouched here, so the list's own layout can't shift.
   ::before (not ::after, which vue-multiselect already uses for its select/deselect hint)
   renders the tooltip text, shown only on hover of that same full row. */
.multiselect--tooltip-overflow >>> .multiselect__option[data-tooltip]:not([data-tooltip=''])::before {
  content: attr(data-tooltip);
  display: none;
  position: absolute;
  left: 12px;
  top: calc(100% + 4px);
  z-index: 1000;
  background: var(--dark);
  color: var(--white);
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: normal;
  white-space: normal;
  max-width: 220px;
  width: max-content;
  pointer-events: none;
  line-height: 1.5;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}

.multiselect--tooltip-overflow >>> .multiselect__option[data-tooltip]:not([data-tooltip='']):hover::before {
  display: block;
}
</style>



// WEBPACK FOOTER //
// src/components/FormFields/FSearchSelect.vue